import {
  SelectHTMLAttributes,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styles from "./CustomDropdown.module.css";
import DropdownIcon from "../../assets/icons/DropdownIcon";
import useOutsideClick from "../../customHooks/useOutsideClick";
import { FormikProps, getIn } from "formik";
import CustomInputField from "../CustomInputField/CustomInputField";
import useAutoFocusInput from "../../customHooks/useAutoFocusInput";
import InputErrorMessage from "../InputErrorMessage";

interface CustomDropdownProps<
  T extends object,
  O,
> extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  extraClasses: string;
  extraDisplayClasses: string;
  options: (string | T)[];
  value: string | string[];
  onClickOption: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSelectOptions: (options: string[]) => void;
  labelKey: keyof T;
  valueKey: keyof T;
  errorMessage: string;
  disabled: boolean;
  formik: FormikProps<O>;
  placeholder: string;
}
const CustomDropdown = <T extends object, O>({
  label,
  placeholder,
  multiple,
  options = [],
  extraClasses = "",
  extraDisplayClasses = "",
  value,
  name,
  labelKey,
  valueKey,
  required,
  errorMessage,
  disabled,
  formik,
  onClickOption,
  onSelectOptions,
}: Partial<CustomDropdownProps<T, O>>) => {
  const { ref, isComponentVisible, setIsComponentVisible } =
    useOutsideClick(false);
  const { elementRef } =
    useAutoFocusInput<HTMLInputElement>(isComponentVisible);
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<(string | T)[]>(() => [
    ...options,
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedValue = value;
  const placeholderText = placeholder || "Select";
  const returnErrorMessage = () => {
    const errStr = formik ? "" : (errorMessage ?? "");

    if (formik) {
      const touched = getIn(formik.touched, name);
      const error = getIn(formik.errors, name);
      return touched && typeof error === "string" ? error : errStr;
    }
    return errStr;
  };
  const errMsg = returnErrorMessage();

  const handleClickDropdown = () => {
    setIsTouched(true);
    setIsComponentVisible((prev) => !prev);
  };

  const handleClickOption = (option: string | T) => {
    if (!option) {
      if (multiple) {
        setSelectedOptions([]);
        onSelectOptions([]);
      } else {
        setSelectedOption("");
        onClickOption &&
          onClickOption({
            target: { name, value: "" },
          } as React.ChangeEvent<HTMLSelectElement>);
      }
      return;
    }
    const labelOption: string = option?.[labelKey as string] || option;
    const valueOption: string = option?.[valueKey as string] || option;
    if (multiple) {
      const labelOptions = selectedOptions.includes(labelOption)
        ? [...selectedOptions].filter((item) => item !== option)
        : [...selectedOptions, labelOption];
      const valueOptions = selectedOptions.includes(valueOption)
        ? [...selectedOptions].filter((item) => item !== option)
        : [...selectedOptions, valueOption];
      setSelectedOptions(labelOptions);
      onSelectOptions(valueOptions);
      return;
    }

    setSelectedOption(labelOption);
    onClickOption &&
      onClickOption({
        target: { name, value: valueOption },
      } as React.ChangeEvent<HTMLSelectElement>);
    setFilteredOptions(options);
  };

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const lowerCaseValue = value?.toLowerCase();
    setFilteredOptions(() =>
      !!value
        ? [...options].filter((option) =>
            typeof option === "string"
              ? option?.toLowerCase().includes(lowerCaseValue)
              : option?.[labelKey as string]
                  .toLowerCase()
                  .includes(lowerCaseValue),
          )
        : options,
    );
  };

  useLayoutEffect(() => {
    if (isComponentVisible && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropDirection("up");
      } else {
        setDropDirection("down");
      }
    } else {
      setDropDirection("down");
    }
    setFilteredOptions(options);
  }, [isComponentVisible, options]);

  useEffect(() => {
    if (typeof selectedValue === "string") {
      setSelectedOption(selectedValue);
      setSelectedOptions([]);
    } else {
      setSelectedOption("");
      setSelectedOptions(selectedValue ?? []);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (
      !formik ||
      !isTouched ||
      isComponentVisible ||
      selectedValue ||
      !required
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      formik.setFieldTouched(name as string, true);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    formik?.setFieldTouched,
    isTouched,
    isComponentVisible,
    selectedValue,
    required,
    name,
  ]);
  return (
    <div
      ref={ref}
      className={`${styles.dropdownWrapper} ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      } focus:outline-none focus:ring-2 focus:ring-blue-500 ${extraClasses}`}
      title={
        required && !selectedOption
          ? `Please select an option${name ? ` for ${name}` : ""}`
          : selectedOption
      }
      tabIndex={0}
    >
      {label && (
        <span className={styles.dropdownLabel}>
          {label} {required && <strong className="text-danger">*</strong>}
        </span>
      )}
      <div
        onClick={handleClickDropdown}
        className={`${styles.dropdownDisplay} ${
          disabled && "disabled"
        } ${extraDisplayClasses}`}
      >
        <div className="d-flex align-items-center justify-content-between w-100 text-nowrap overflow-auto hide-scrollbar">
          {multiple ? (
            !!selectedOptions.length ? (
              <div>
                {selectedOptions.map((option) => (
                  <span>{option}</span>
                ))}
              </div>
            ) : (
              <span>{placeholderText}</span>
            )
          ) : (
            <span
              className={`${styles.dropdownPlaceholder} ${
                styles[selectedOption ? "selected" : "notSelected"]
              }`}
            >
              {selectedOption || placeholderText}
            </span>
          )}
          <DropdownIcon
            className={`${styles.dropdownIcon} ${
              styles[isComponentVisible ? "listOpen" : ""]
            }`}
          />
        </div>
        {isComponentVisible && (
          <div
            ref={dropdownRef}
            className={`${styles.dropdownList} ${styles[dropDirection]}`}
          >
            <ul>
              {options.length > 10 && (
                <li
                  className={styles.searchInput}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CustomInputField
                    placeholder="Search"
                    type="search"
                    onChange={onChangeSearch}
                    ref={elementRef}
                  />
                </li>
              )}
              <li onClick={() => handleClickOption("")} key={placeholderText}>
                {placeholderText}
              </li>
              {filteredOptions.map((option, index) => (
                <li
                  key={(option?.[valueKey as string] || option) + index}
                  onClick={() => handleClickOption(option)}
                  title={option?.[labelKey as string] || option}
                >
                  {option?.[labelKey as string] || option}
                </li>
              ))}
            </ul>
          </div>
        )}
        <InputErrorMessage errorMessage={errMsg} />
      </div>
    </div>
  );
};

export default CustomDropdown;
